import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  User,
  Package,
  Settings,
  LogOut,
  Loader2,
  Check,
  Plus,
  MapPin,
  Pencil,
  Trash2,
  ChevronRight,
  Star,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  fetchAddresses,
  addAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
} from '../services/addressService';
import { fetchOrdersByUser } from '../services/orderService';
import { INDIAN_STATES } from '../utils/indianStates';

const TABS = [
  { key: 'profile', label: 'Profile', icon: User },
  { key: 'orders', label: 'Orders', icon: Package },
  { key: 'settings', label: 'Settings', icon: Settings },
];

function Field({ label, error, readOnly, ...props }) {
  const isTextarea = props.as === 'textarea';
  const inputProps = { ...props };
  delete inputProps.as;

  const baseClass =
    'w-full border-b py-2.5 text-sm bg-transparent transition-colors font-light outline-none';
  const readOnlyClass = readOnly ? 'text-gray-400 cursor-not-allowed border-gray-100' : 'focus:border-black border-gray-200';

  return (
    <div>
      <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-1.5">
        {label}
      </label>
      {isTextarea ? (
        <textarea
          {...inputProps}
          rows={3}
          className={`${baseClass} ${readOnlyClass} resize-none ${error ? 'border-red-400' : ''}`}
        />
      ) : (
        <input
          {...inputProps}
          readOnly={readOnly}
          className={`${baseClass} ${readOnlyClass} ${error ? 'border-red-400' : ''}`}
        />
      )}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

/* ─── Address Card ─── */
function AddressCard({ address, onEdit, onDelete, onSetDefault, isDefault }) {
  return (
    <div className={`border p-5 ${isDefault ? 'border-black' : 'border-gray-200'} rounded`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs tracking-[0.15em] uppercase font-medium text-gray-500">
              {address.label || 'Address'}
            </span>
            {isDefault && (
              <span className="text-[10px] tracking-widest uppercase bg-black text-white px-2 py-0.5">
                Default
              </span>
            )}
          </div>
          <p className="text-sm font-medium">{address.name}</p>
          <p className="text-sm text-gray-500 mt-0.5">{address.phone}</p>
          <p className="text-sm text-gray-500 mt-1 leading-relaxed">
            {address.address}, {address.city}, {address.state} – {address.pincode}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
        <button
          onClick={() => onEdit(address)}
          className="flex items-center gap-1.5 text-xs tracking-widest uppercase text-gray-500 hover:text-black transition-colors"
        >
          <Pencil size={12} /> Edit
        </button>
        {!isDefault && (
          <>
            <button
              onClick={() => onSetDefault(address.id)}
              className="flex items-center gap-1.5 text-xs tracking-widest uppercase text-gray-500 hover:text-black transition-colors"
            >
              <Star size={12} /> Set Default
            </button>
            <button
              onClick={() => onDelete(address.id)}
              className="flex items-center gap-1.5 text-xs tracking-widest uppercase text-red-400 hover:text-red-600 transition-colors"
            >
              <Trash2 size={12} /> Remove
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Address Form Modal ─── */
function AddressFormModal({ show, onClose, onSave, initial, title }) {
  const [form, setForm] = useState({
    label: 'Home',
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const labelOptions = ['Home', 'Work', 'Other'];

  useEffect(() => {
    if (initial) {
      setForm({
        label: initial.label || 'Home',
        name: initial.name || '',
        phone: initial.phone || '',
        address: initial.address || '',
        city: initial.city || '',
        state: initial.state || '',
        pincode: initial.pincode || '',
      });
    } else {
      setForm({ label: 'Home', name: '', phone: '', address: '', city: '', state: '', pincode: '' });
    }
    setErrors({});
  }, [initial, show]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ''))) e.phone = 'Enter a valid 10-digit number';
    if (!form.address.trim()) e.address = 'Address is required';
    if (!form.city.trim()) e.city = 'City is required';
    if (!form.state.trim()) e.state = 'State is required';
    if (!/^\d{6}$/.test(form.pincode)) e.pincode = 'Enter a valid 6-digit pincode';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSaving(true);
    try {
      await onSave(form, initial?.id);
      onClose();
    } catch (err) {
      setErrors({ form: err.message || 'Could not save address.' });
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl font-light">{title || 'Add Address'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-black text-xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-2">Label</label>
            <div className="flex gap-3">
              {labelOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, label: opt }))}
                  className={`px-4 py-2 text-xs tracking-widest uppercase border transition-colors ${
                    form.label === opt
                      ? 'border-black bg-black text-white'
                      : 'border-gray-200 text-gray-500 hover:border-gray-400'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Full Name" name="name" value={form.name} onChange={handleChange} error={errors.name} placeholder="Your name" />
            <Field
              label="Phone"
              name="phone"
              inputMode="numeric"
              value={form.phone}
              onChange={(e) =>
                handleChange({
                  target: { name: 'phone', value: e.target.value.replace(/\D/g, '').slice(0, 10) },
                })
              }
              error={errors.phone}
              placeholder="9876543210"
            />
          </div>
          <Field label="Address" name="address" as="textarea" value={form.address} onChange={handleChange} error={errors.address} placeholder="House no., street, area" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <Field label="City" name="city" value={form.city} onChange={handleChange} error={errors.city} placeholder="City" />
            <div>
              <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-1.5">State</label>
              <select
                name="state"
                value={form.state}
                onChange={handleChange}
                className={`w-full border-b py-2.5 text-sm bg-transparent outline-none font-light transition-colors ${
                  errors.state ? 'border-red-400' : 'border-gray-200 focus:border-black'
                } ${!form.state ? 'text-gray-400' : 'text-black'}`}
              >
                <option value="" disabled>Select state</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s} value={s} className="text-black">{s}</option>
                ))}
              </select>
              {errors.state && <p className="text-xs text-red-400 mt-1">{errors.state}</p>}
            </div>
            <Field
              label="Pincode"
              name="pincode"
              inputMode="numeric"
              value={form.pincode}
              onChange={(e) =>
                handleChange({
                  target: { name: 'pincode', value: e.target.value.replace(/\D/g, '').slice(0, 6) },
                })
              }
              error={errors.pincode}
              placeholder="243601"
            />
          </div>
          {errors.form && <p className="text-xs text-red-500">{errors.form}</p>}
          <button
            type="submit"
            disabled={saving}
            className="w-full bg-black text-white py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {initial ? 'Update Address' : 'Save Address'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Main Profile Page ─── */
export default function Profile() {
  const navigate = useNavigate();
  const { user, profile, authLoading, setLoginModalOpen, saveProfileDetails, logout } = useAuth();

  const [activeTab, setActiveTab] = useState('profile');

  // Profile form
  const [form, setForm] = useState({ full_name: '', email: '' });
  const [profileErrors, setProfileErrors] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Addresses
  const [addresses, setAddresses] = useState([]);
  const [addressLoading, setAddressLoading] = useState(true);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  // Orders
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const phoneDisplay = user?.phoneNumber || profile?.phone || '';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoginModalOpen(true);
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate, setLoginModalOpen]);

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        email: profile.email || '',
      });
    }
  }, [profile]);

  useEffect(() => {
    if (user) {
      loadAddresses();
    }
  }, [user]);

  const loadAddresses = async () => {
    if (!user) return;
    setAddressLoading(true);
    try {
      const data = await fetchAddresses(user.uid);
      setAddresses(data);
    } catch (err) {
      console.error('Failed to load addresses:', err);
    } finally {
      setAddressLoading(false);
    }
  };

  const loadOrders = useCallback(async () => {
    if (!user) return;
    setOrdersLoading(true);
    try {
      const data = await fetchOrdersByUser(user.uid);
      setOrders(data);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'orders' && user) {
      loadOrders();
    }
  }, [activeTab, user, loadOrders]);

  // Profile save
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = {};
    if (!form.full_name.trim()) validationErrors.full_name = 'Full name is required';
    if (!form.email.trim()) {
      validationErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      validationErrors.email = 'Enter a valid email';
    }
    if (Object.keys(validationErrors).length > 0) {
      setProfileErrors(validationErrors);
      return;
    }
    setSavingProfile(true);
    setProfileSaved(false);
    try {
      await saveProfileDetails({
        full_name: form.full_name.trim() || null,
        email: form.email.trim() || null,
      });
      setProfileSaved(true);
    } catch (err) {
      setProfileErrors({ form: 'Could not save profile. Please try again.' });
    } finally {
      setSavingProfile(false);
    }
  };

  // Address handlers
  const handleAddAddress = async (formData) => {
    await addAddress(user.uid, {
      ...formData,
      is_default: addresses.length === 0,
    });
    await loadAddresses();
  };

  const handleUpdateAddress = async (formData, addressId) => {
    await updateAddress(addressId, formData);
    await loadAddresses();
  };

  const handleDeleteAddress = async (addressId) => {
    await deleteAddress(addressId);
    await loadAddresses();
  };

  const handleSetDefault = async (addressId) => {
    await setDefaultAddress(addressId, user.uid);
    await loadAddresses();
  };

  const openEditAddress = (address) => {
    setEditingAddress(address);
    setShowAddressModal(true);
  };

  const openAddAddress = () => {
    setEditingAddress(null);
    setShowAddressModal(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Status helpers
  const STATUS_LABELS = {
    pending: 'Pending',
    paid: 'Confirmed',
    failed: 'Failed',
    cancelled: 'Cancelled',
    shipped: 'Shipped',
    delivered: 'Delivered',
  };

  if (authLoading || !user) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white">
      {/* ─── Header ─── */}
      <section className="bg-[#fdf8f6] py-16 sm:py-20 text-center px-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border border-gray-100 mb-4">
          <User size={22} className="text-charcoal" />
        </div>
        <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">Your Account</p>
        <h1 className="font-display text-4xl sm:text-5xl font-light">My Account</h1>
        {phoneDisplay && (
          <p className="text-sm text-gray-500 font-light mt-3">{phoneDisplay}</p>
        )}
      </section>

      {/* ─── Tab Bar ─── */}
      <div className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex gap-0 -mb-px">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 text-xs tracking-[0.2em] uppercase font-medium border-b-2 transition-colors ${
                    isActive
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Tab Content ─── */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* ═══ PROFILE TAB ═══ */}
        {activeTab === 'profile' && (
          <div className="space-y-12">
            {/* Profile Info (Read-Only after save) */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <User size={16} className="text-gray-400" />
                <h2 className="text-xs tracking-[0.2em] uppercase font-medium">Personal Information</h2>
              </div>
              <div className="space-y-6 max-w-lg">
                <Field
                  label="Full Name"
                  name="full_name"
                  value={form.full_name}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, full_name: e.target.value }));
                    setProfileSaved(false);
                  }}
                  placeholder="Your name"
                  error={profileErrors.full_name}
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, email: e.target.value }));
                    setProfileSaved(false);
                  }}
                  placeholder="you@example.com"
                  error={profileErrors.email}
                />
                <Field
                  label="Phone (verified)"
                  value={phoneDisplay}
                  readOnly
                />
                {profileErrors.form && <p className="text-xs text-red-500">{profileErrors.form}</p>}
                <button
                  onClick={handleProfileSubmit}
                  disabled={savingProfile}
                  className="bg-black text-white px-8 py-3 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingProfile ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : profileSaved ? (
                    <>
                      <Check size={16} /> Saved
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>

            {/* Addresses */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-gray-400" />
                  <h2 className="text-xs tracking-[0.2em] uppercase font-medium">Saved Addresses</h2>
                </div>
                <button
                  onClick={openAddAddress}
                  className="flex items-center gap-1.5 text-xs tracking-widest uppercase border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors"
                >
                  <Plus size={12} /> Add New
                </button>
              </div>
              {addressLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 size={20} className="animate-spin text-gray-400" />
                </div>
              ) : addresses.length === 0 ? (
                <div className="border border-dashed border-gray-200 p-10 text-center">
                  <MapPin size={28} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-sm text-gray-400 font-light">No saved addresses yet.</p>
                  <p className="text-xs text-gray-300 mt-1">Add an address for faster checkout.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {addresses.map((addr) => (
                    <AddressCard
                      key={addr.id}
                      address={addr}
                      isDefault={addr.is_default}
                      onEdit={openEditAddress}
                      onDelete={handleDeleteAddress}
                      onSetDefault={handleSetDefault}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ ORDERS TAB ═══ */}
        {activeTab === 'orders' && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Package size={16} className="text-gray-400" />
              <h2 className="text-xs tracking-[0.2em] uppercase font-medium">Order History</h2>
            </div>
            {ordersLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : orders.length === 0 ? (
              <div className="border border-dashed border-gray-200 p-12 text-center">
                <Package size={32} className="mx-auto text-gray-300 mb-3" />
                <p className="text-sm text-gray-400 font-light">No orders yet.</p>
                <Link
                  to="/shop"
                  className="inline-block mt-4 text-xs tracking-widest uppercase border-b border-black pb-0.5 hover:text-gray-500 transition-colors"
                >
                  Start Shopping
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <Link
                    key={order.id}
                    to={`/order-success/${order.id}`}
                    className="block border border-gray-200 p-5 hover:border-black transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="text-sm font-medium">{order.order_number}</p>
                          <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 border ${
                            order.status === 'paid' || order.status === 'delivered'
                              ? 'border-green-200 text-green-700 bg-green-50'
                              : order.status === 'failed' || order.status === 'cancelled'
                              ? 'border-red-200 text-red-600 bg-red-50'
                              : 'border-gray-200 text-gray-500 bg-gray-50'
                          }`}>
                            {STATUS_LABELS[order.status] || order.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400">
                          {new Date(order.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </p>
                        <p className="text-sm mt-2">
                          ₹{order.total_amount.toLocaleString()} · {order.items?.length || 0} item(s)
                        </p>
                      </div>
                      <ChevronRight size={16} className="text-gray-400 shrink-0 mt-1" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ SETTINGS TAB ═══ */}
        {activeTab === 'settings' && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Settings size={16} className="text-gray-400" />
              <h2 className="text-xs tracking-[0.2em] uppercase font-medium">Account Settings</h2>
            </div>
            <div className="space-y-4 max-w-md">
              <div className="border border-gray-200 p-5">
                <p className="text-sm font-medium mb-1">Account</p>
                <p className="text-xs text-gray-400 mb-4">
                  Signed in as {phoneDisplay}
                </p>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-xs tracking-widest uppercase text-red-500 hover:text-red-700 transition-colors"
                >
                  <LogOut size={14} /> Log Out
                </button>
              </div>
              <div className="border border-gray-200 p-5">
                <p className="text-sm font-medium mb-1">Need Help?</p>
                <p className="text-xs text-gray-400 mb-3">
                  Contact us for any account-related issues.
                </p>
                <Link
                  to="/contact"
                  className="text-xs tracking-widest uppercase border-b border-black pb-0.5 hover:text-gray-500 transition-colors"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Address Modal ─── */}
      <AddressFormModal
        show={showAddressModal}
        onClose={() => {
          setShowAddressModal(false);
          setEditingAddress(null);
        }}
        onSave={editingAddress ? handleUpdateAddress : handleAddAddress}
        initial={editingAddress}
        title={editingAddress ? 'Edit Address' : 'Add New Address'}
      />
    </main>
  );
}